import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
  type ReactNode,
} from "react";
import { apiRequest } from "../services/api/client";
import type { AxiosRequestConfig } from "axios";

// Define the shape of our cache entries
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  requestId: string;
}

// Define the shape of our pending requests
interface PendingRequest<T> {
  promise: Promise<T>;
  requestId: string;
}

// Define the context value shape
interface APIContextValue {
  request: <T>(
    config: AxiosRequestConfig,
    cacheOptions?: CacheOptions
  ) => Promise<T>;
  clearCache: (pattern?: string) => void;
  invalidateCache: (pattern?: string) => void;
  isLoading: (requestId?: string) => boolean;
}

// Cache options for API requests
interface CacheOptions {
  enabled?: boolean;
  ttl?: number; // Time to live in milliseconds
  key?: string; // Custom cache key
}

// Create the context
const APIContext = createContext<APIContextValue | null>(null);

// Provider props
interface APIProviderProps {
  children: ReactNode;
  cacheTTL?: number; // Default cache TTL in milliseconds
  debug?: boolean;
}

// Generate a cache key from a request config
const generateCacheKey = (config: AxiosRequestConfig): string => {
  const { method = "GET", url = "", params = {}, data = {} } = config;
  return `${method}:${url}:${JSON.stringify(params)}:${JSON.stringify(data)}`;
};

export const APIProvider: React.FC<APIProviderProps> = ({
  children,
  cacheTTL = 5 * 60 * 1000, // 5 minutes default
  debug = false,
}) => {
  // Cache for storing API responses
  const cache = useRef<Map<string, CacheEntry<any>>>(new Map());

  // Track pending requests to avoid duplicate calls
  const pendingRequests = useRef<Map<string, PendingRequest<any>>>(new Map());

  // Track loading state for requests
  const [loadingRequests, setLoadingRequests] = useState<Set<string>>(
    new Set()
  );

  // Debug logging
  const log = useCallback(
    (...args: any[]) => {
      if (debug) {
        // console.log("[APIContext]", ...args);
      }
    },
    [debug]
  );

  // Check if a cache entry is still valid
  const isCacheValid = useCallback(
    (entry: CacheEntry<any>): boolean => {
      return Date.now() - entry.timestamp < cacheTTL;
    },
    [cacheTTL]
  );

  // Make an API request with caching
  const request = useCallback(
    async <T,>(
      config: AxiosRequestConfig,
      cacheOptions: CacheOptions = {}
    ): Promise<T> => {
      const { enabled = true, ttl = cacheTTL, key: customKey } = cacheOptions;

      // Generate a unique ID for this request
      const requestId = Math.random().toString(36).substring(2, 9);

      // Generate cache key
      const cacheKey = customKey || generateCacheKey(config);

      // Update loading state
      setLoadingRequests((prev) => {
        const newSet = new Set(prev);
        newSet.add(requestId);
        return newSet;
      });

      try {
        // Check if caching is enabled and we have a valid cached response
        if (enabled) {
          const cachedEntry = cache.current.get(cacheKey);
          if (cachedEntry && isCacheValid(cachedEntry)) {
            log("Cache hit:", cacheKey);

            // Remove from loading state after a short delay to prevent UI flicker
            setTimeout(() => {
              setLoadingRequests((prev) => {
                const newSet = new Set(prev);
                newSet.delete(requestId);
                return newSet;
              });
            }, 0);

            return cachedEntry.data;
          }
          log("Cache miss:", cacheKey);
        }

        // Check if we already have a pending request for this exact config
        if (pendingRequests.current.has(cacheKey)) {
          log("Reusing pending request:", cacheKey);
          const pendingRequest = pendingRequests.current.get(cacheKey)!;

          // Wait for the existing request to complete
          const result = await pendingRequest.promise;

          // Update loading state
          setLoadingRequests((prev) => {
            const newSet = new Set(prev);
            newSet.delete(requestId);
            return newSet;
          });

          return result;
        }

        // Create a new request
        const requestPromise = apiRequest<T>(config);

        // Store the pending request
        pendingRequests.current.set(cacheKey, {
          promise: requestPromise,
          requestId,
        });

        // Wait for the request to complete
        const response = await requestPromise;

        // Cache the response if caching is enabled
        if (enabled) {
          cache.current.set(cacheKey, {
            data: response,
            timestamp: Date.now(),
            requestId,
          });
          log("Cached response for:", cacheKey);
        }

        // Remove from pending requests
        pendingRequests.current.delete(cacheKey);

        return response;
      } finally {
        // Update loading state
        setLoadingRequests((prev) => {
          const newSet = new Set(prev);
          newSet.delete(requestId);
          return newSet;
        });
      }
    },
    [cacheTTL, isCacheValid, log]
  );

  // Clear the entire cache or entries matching a pattern
  const clearCache = useCallback(
    (pattern?: string) => {
      if (!pattern) {
        log("Clearing entire cache");
        cache.current.clear();
        return;
      }

      log("Clearing cache with pattern:", pattern);
      const keysToDelete: string[] = [];

      cache.current.forEach((_, key) => {
        if (key.includes(pattern)) {
          keysToDelete.push(key);
        }
      });

      keysToDelete.forEach((key) => {
        cache.current.delete(key);
      });
    },
    [log]
  );

  // Invalidate cache entries (mark them as expired)
  const invalidateCache = useCallback(
    (pattern?: string) => {
      if (!pattern) {
        log("Invalidating entire cache");
        cache.current.clear();
        return;
      }

      log("Invalidating cache with pattern:", pattern);
      cache.current.forEach((entry, key) => {
        if (key.includes(pattern)) {
          // Set timestamp to 0 to invalidate
          entry.timestamp = 0;
        }
      });
    },
    [log]
  );

  // Check if a request is loading
  const isLoading = useCallback(
    (requestId?: string) => {
      if (requestId) {
        return loadingRequests.has(requestId);
      }
      return loadingRequests.size > 0;
    },
    [loadingRequests]
  );

  // Create the context value
  const contextValue: APIContextValue = {
    request,
    clearCache,
    invalidateCache,
    isLoading,
  };

  return (
    <APIContext.Provider value={contextValue}>{children}</APIContext.Provider>
  );
};

// Hook to use the API context
export const useAPI = () => {
  const context = useContext(APIContext);
  if (!context) {
    throw new Error("useAPI must be used within an APIProvider");
  }
  return context;
};
